import { ArrowBackIos, Save } from '@mui/icons-material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectButton } from 'primereact/selectbutton';
import { toast } from 'react-toastify';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { Header } from '../../../components/header/header';
import {
  ErrorText,
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../../components/layout/main/mainStyles';
import Strings from '../../../i18n';
import { ButtonIconStyled } from '../../locations/allLocationsList/allLocationListStyles';
import { TooltipStyled } from '../../organizations/organizationsLocs/organizationsLocStyles';
import {
  useAddFirmwareVersionMutation,
  useEditFirmwareVersionMutation,
  useGetFirmwareVersionDetailsMutation
} from '../../../services/firmwareVersions/firmwareVersionsService';
import { addEditFirmwareVersionValidator } from '../../../utils/validators';
import { StyledInputLabel } from '../versionTemplateStyles';

export const AddEditFirmwareVersionForm = () => {
  const navigate = useNavigate();
  const { versionId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const items = [
    { name: 'Development', value: false },
    { name: 'Production', value: true }
  ];
  const [firmwareVersion, setFirmwareVersion] = useState<{
    name: string;
    isProduction: boolean;
    active: boolean;
    minWifiModuleVersionName: string;
    id?: number;
  }>({
    name: '',
    minWifiModuleVersionName: '',
    isProduction: false,
    active: true
  });

  const [
    getFirmwareVersionDetails,
    { isSuccess: successGetData, data: dataFirmwareVersion }
  ] = useGetFirmwareVersionDetailsMutation();

  const [
    postFirmwareVersion,
    { isSuccess: successPost, data: successPostData }
  ] = useAddFirmwareVersionMutation();

  const [editFirmwareVersion, { isSuccess: successEdit, data: dataEdit }] =
    useEditFirmwareVersionMutation();

  useEffect(() => {
    if (versionId) {
      getFirmwareVersionDetails(versionId);
      setIsEditing(true);
    }
  }, [versionId, getFirmwareVersionDetails]);

  useEffect(() => {
    if (successGetData && dataFirmwareVersion) {
      setFirmwareVersion({
        name: dataFirmwareVersion.name,
        minWifiModuleVersionName: dataFirmwareVersion.minWifiModuleVersionName,
        isProduction: dataFirmwareVersion.isProduction,
        active: dataFirmwareVersion.active
      });
    }
  }, [successGetData, dataFirmwareVersion]);

  const save = () => {
    document.getElementById('submitForm')?.click();
  };

  const sendInfo = (values: {
    name: string;
    minWifiModuleVersionName: string;
  }) => {
    if (!isEditing) {
      const payload = {
        ...values,
        isProduction: firmwareVersion.isProduction,
        active: firmwareVersion.active
      };
      postFirmwareVersion(payload);
    } else {
      const payload = {
        ...values,
        isProduction: firmwareVersion.isProduction,
        active: firmwareVersion.active,
        id: versionId
      };
      if (versionId) {
        editFirmwareVersion({ body: payload, versionId: +versionId });
      }
    }
  };

  useEffect(() => {
    if (successPost && successPostData) {
      toast.success(Strings.versions.successAddFirmwareVersion);
      navigate(`/firmwareVersions/${successPostData}/files`);
    }
  }, [successPost]);

  useEffect(() => {
    if (successEdit) {
      toast.success(Strings.versions.successEditFirmwareVersion);
      navigate(`/firmwareVersions/${versionId}/files`);
    }
  }, [successEdit]);
  return (
    <div>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled size="small" onClick={() => navigate(-1)}>
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.serverAccess.menu.versions}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              <Tooltip
                title={Strings.common.save}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled size="small" onClick={save}>
                  <Save />
                </ButtonIconStyled>
              </Tooltip>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <div className="row justify-content-center">
        <div className="col-11 col-10">
          <Formik
            enableReinitialize
            validationSchema={addEditFirmwareVersionValidator}
            initialValues={{
              name: firmwareVersion.name,
              minWifiModuleVersionName: firmwareVersion.minWifiModuleVersionName
            }}
            onSubmit={(values) => {
              sendInfo(values);
            }}
          >
            {({
              handleChange,
              handleSubmit,
              values,
              isValid,
              setFieldTouched,
              errors,
              touched
            }) => (
              <div>
                <div>
                  <StyledInputLabel>
                    {Strings.versions.versionName}
                  </StyledInputLabel>
                  <InputText
                    type="text"
                    id="name"
                    name="name"
                    value={values.name}
                    onChange={handleChange('name')}
                    onBlur={() => setFieldTouched('name')}
                    className="w-100"
                    disabled={isEditing}
                  />
                  {errors.name && touched.name && (
                    <ErrorText>{errors.name}</ErrorText>
                  )}
                </div>
                <div className="mt-3">
                  <SelectButton
                    value={firmwareVersion.isProduction}
                    onChange={(e) => {
                      setFirmwareVersion({
                        ...firmwareVersion,
                        isProduction: e.value
                      });
                    }}
                    options={items}
                    optionLabel="name"
                  />
                </div>
                <div className="mt-3">
                  <Checkbox
                    name="activeFw"
                    value={firmwareVersion.active}
                    onChange={(e) => {
                      setFirmwareVersion({
                        ...firmwareVersion,
                        active: e.checked as boolean
                      });
                    }}
                    checked={firmwareVersion.active}
                  />
                  <span className="ml-2">Make active</span>
                </div>

                <div>
                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    id="submitForm"
                    className="d-none"
                  >
                    {Strings.common.save}
                  </button>
                </div>
              </div>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
